import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

import type TextRenderer from "bpmn-js/lib/draw/TextRenderer";
import LabelEditingProvider from "bpmn-js/lib/features/label-editing/LabelEditingProvider";
import type BpmnFactory from "bpmn-js/lib/features/modeling/BpmnFactory";
import type Modeling from "bpmn-js/lib/features/modeling/Modeling";
import { Element as BPMNElement } from "bpmn-js/lib/model/Types";
import { getBusinessObject } from "bpmn-js/lib/util/ModelUtil";
import type Canvas from "diagram-js/lib/core/Canvas";
import type EventBus from "diagram-js/lib/core/EventBus";
import type DirectEditing from "diagram-js/lib/features/direct-editing/DirectEditing";
import type ResizeHandles from "diagram-js/lib/features/resize/ResizeHandles";
import { ShapeLike } from "diagram-js/lib/model/Types";
import { createPinia } from "pinia";
import { App, createApp, markRaw } from "vue";

import ReferenceSelector from "@/components/BPMN/ReferenceSelector.vue";
import {
    elementIsCallActivity,
    elementIsCollapsedPool,
} from "@/composables/bpmn-manager/helpers";
import { initPrimeVue } from "@/primevue/init";
import router from "@/router";
import FontAwesomeButton from "@/shared/FontAwesomeButton.vue";
import { EntityType } from "@/stores/entity-store";

const MARKER_HIDDEN = "djs-element-hidden";
const MARKER_LABEL_HIDDEN = "djs-label-hidden";

export default class CustomLabelEditingProvider extends LabelEditingProvider {
    _app: App | null = null;
    _el: HTMLElement | null = null;
    _modeling: Modeling;
    _canvas: Canvas;
    _eventBus: EventBus;
    _directEditing: DirectEditing;

    constructor(
        eventBus: EventBus,
        bpmnFactory: BpmnFactory,
        canvas: Canvas,
        directEditing: DirectEditing,
        modeling: Modeling,
        resizeHandles: ResizeHandles,
        textRenderer: TextRenderer
    ) {
        super(
            eventBus,
            bpmnFactory,
            canvas,
            directEditing,
            modeling,
            resizeHandles,
            textRenderer
        );
        this._canvas = canvas;
        this._modeling = modeling;
        this._eventBus = eventBus;
        this._directEditing = directEditing;

        // Listen for deactivation to clean up our Vue component
        eventBus.on("directEditing.deactivate", () => {
            // Use setTimeout to ensure we don't unmount while an event is still processing
            // (e.g. PrimeVue trying to focus back after hide)
            setTimeout(() => {
                this.cleanup();
            }, 0);
        });

        // Ensure label stays visible even if LabelEditingPreview tries to hide it.
        // We use a lower priority (500) to run after LabelEditingPreview (1000).
        eventBus.on(
            "directEditing.activate",
            500,
            (context: { active: { element: ShapeLike } }) => {
                const element = context.active.element;
                if (
                    elementIsCallActivity(element) ||
                    elementIsCollapsedPool(element)
                ) {
                    // Use setTimeout to ensure we run after the next tick where markers might be added
                    setTimeout(() => {
                        canvas.removeMarker(
                            element.label || element,
                            MARKER_HIDDEN
                        );
                        canvas.removeMarker(
                            element.label || element,
                            MARKER_LABEL_HIDDEN
                        );
                    });
                }
            }
        );
    }

    activate(element: BPMNElement) {
        if (elementIsCallActivity(element) || elementIsCollapsedPool(element)) {
            this.initVueComponent(element);

            const bo = getBusinessObject(element);

            // Return a dummy context so directEditing considers itself active.
            // We use display: 'none' to ensure the hidden default textbox
            // doesn't intercept any mouse events or hide the text.
            return {
                bounds: { x: 0, y: 0, width: 0, height: 0 },
                text: bo.name || "",
                style: {
                    display: "none",
                },
            };
        }

        // Fallback to default behavior for all other elements
        return super.activate(element);
    }

    update(
        element: BPMNElement,
        newLabel: string,
        activeContextText: string,
        bounds: unknown
    ) {
        if (elementIsCallActivity(element) || elementIsCollapsedPool(element)) {
            // We handle updates in the Vue component (ReferenceSelector.vue),
            // so we ignore the default update call from DirectEditing.
            return;
        }
        super.update(element, newLabel, activeContextText, bounds);
    }

    initVueComponent(element: BPMNElement) {
        this.cleanup(); // Ensure any existing instance is removed

        const container = this._canvas.getContainer();
        const el = document.createElement("div");
        this._el = el;

        const viewbox = this._canvas.viewbox();

        el.style.position = "absolute";
        el.style.left = `${(element.x - viewbox.x) * viewbox.scale}px`;
        el.style.top = `${(element.y - viewbox.y) * viewbox.scale}px`;
        el.style.width = `${element.width * viewbox.scale}px`;
        el.style.height = `${element.height * viewbox.scale}px`;
        el.style.zIndex = "100";
        // Enable pointer events so the TreeSelect can be clicked
        el.style.pointerEvents = "auto";

        container.appendChild(el);

        const isCallActivity = elementIsCallActivity(element);

        this._app = createApp(ReferenceSelector, {
            element: markRaw(element),
            modeling: markRaw(this._modeling),
            eventBus: markRaw(this._eventBus),
            allowedEntityTypes: isCallActivity
                ? [EntityType.PROCESS]
                : [EntityType.DOMAIN, EntityType.PRODUCT, EntityType.PROCESS],
            onClose: () => {
                this._directEditing.complete();
            },
            allowCustomValue: !isCallActivity,
        });

        initPrimeVue(this._app);
        this._app.use(router);
        const pinia = createPinia();
        this._app.use(pinia);
        this._app.component("FontAwesomeIcon", FontAwesomeIcon);
        this._app.component("FontAwesomeButton", FontAwesomeButton);

        this._app.mount(el);
    }

    cleanup() {
        if (this._app) {
            this._app.unmount();
            this._app = null;
        }
        if (this._el) {
            this._el.remove();
            this._el = null;
        }
    }
}

CustomLabelEditingProvider.$inject = [
    "eventBus",
    "bpmnFactory",
    "canvas",
    "directEditing",
    "modeling",
    "resizeHandles",
    "textRenderer",
];
